
import React, { useState } from 'react';
import { checklistDocumentToText, termsDocumentToText } from '../content/legalDocuments';
import { FileText, Download, Search, FolderOpen, ChevronRight, FileCheck, ClipboardList, Info } from 'lucide-react';

interface DocumentTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  content: string;
}

const Repository: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

  const templates: DocumentTemplate[] = [
    {
      id: '1',
      name: 'Termo de Consentimento para Registro Digital',
      category: 'Ético / Legal',
      description: 'Autorização específica para uso de gravação e transcrição de áudio via sistema digital de apoio clínico.',
      content: `TERMO DE CONSENTIMENTO PARA USO DE SISTEMA DIGITAL DE REGISTRO CLÍNICO

Eu, _______________________________________, inscrito no CPF nº: ____________________, aqui identificado como PACIENTE, declaro que fui informado(a) de forma clara sobre o uso de sistema digital de apoio clínico utilizado pelo profissional responsável por meu atendimento.

Estou ciente de que:

• As sessões poderão ser gravadas em áudio exclusivamente para fins terapêuticos, registro clínico e acompanhamento da evolução do tratamento.
• Os áudios poderão ser transcritos automaticamente por sistema computacional, gerando textos e indicadores clínicos de apoio ao profissional.
• Esses dados incluem informações sensíveis relacionadas à minha saúde mental.
• O acesso aos dados é restrito ao profissional responsável e ao sistema utilizado, respeitando padrões de segurança.
• Nenhum conteúdo será utilizado para fins comerciais, marketing ou divulgação.
• Posso solicitar a exclusão dos meus dados a qualquer momento.
• Posso revogar este consentimento sem prejuízo ao meu atendimento.

Autorizo de forma livre, informada e inequívoca o uso dessas tecnologias conforme descrito.

Cidade, dia, mês de ano.


__________________________
Nome:
PACIENTE`
    },
    {
      id: '2',
      name: 'Termos de Uso e Privacidade - Allodos',
      category: 'Ético / Legal',
      description: 'Termos e Condições de Uso e Política de Privacidade – Plataforma Allodos (última atualização: 11 de setembro de 2026), apresentados no primeiro acesso.',
      // Redação integral do DOCX do Product Owner (bloco 30), mesma fonte da
      // tela de primeiro acesso.
      content: termsDocumentToText(),
    },
    {
      id: '3',
      name: 'Checklist de Conformidade LGPD',
      category: 'Gestão / Ética',
      description: 'Checklist LGPD e Conformidade Ética – Allodos: responsabilidade do usuário (controlador de dados e responsável técnico), apresentado no primeiro acesso.',
      // Redação integral do DOCX do Product Owner (bloco 31).
      content: checklistDocumentToText(),
    }
  ];

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black dark:text-white tracking-tight">Repositório de Documentos</h1>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">Modelos de documentos e ferramentas para sua prática clínica.</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text" 
            placeholder="Buscar modelos..." 
            className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all dark:text-slate-100"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto pr-2 custom-scrollbar">
          {filteredTemplates.map(template => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              className={`w-full p-6 rounded-[2rem] border-2 text-left transition-all relative group ${selectedTemplate?.id === template.id ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-indigo-300'}`}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className={`p-3 rounded-2xl ${selectedTemplate?.id === template.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                  <FileText size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-black dark:text-white leading-tight">{template.name}</h4>
                  <span className="text-[9px] font-black uppercase tracking-widest text-indigo-500 dark:text-indigo-400 mt-1 block">{template.category}</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{template.description}</p>
            </button>
          ))}
          {filteredTemplates.length === 0 && (
            <div className="p-6 sm:p-12 text-center text-slate-400 italic text-sm">Nenhum modelo encontrado.</div>
          )}
        </div>

        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden flex flex-col h-full min-h-[500px] animate-in slide-in-from-right-4">
              <div className="p-5 sm:p-8 border-b dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white"><FileCheck size={24} /></div>
                  <div>
                    <h3 className="text-xl font-black dark:text-white tracking-tight">{selectedTemplate.name}</h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{selectedTemplate.category}</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    const blob = new Blob([selectedTemplate.content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedTemplate.name.replace(/\s+/g, '_')}.txt`;
                    a.click();
                  }}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg active:scale-95"
                >
                  <Download size={16} /> Baixar Modelo
                </button>
              </div>
              <div className="flex-1 p-5 sm:p-10 overflow-y-auto custom-scrollbar">
                <div className="bg-slate-50 dark:bg-slate-800/50 p-5 sm:p-8 rounded-[2rem] border border-slate-100 dark:border-slate-700 font-mono text-sm leading-relaxed whitespace-pre-wrap dark:text-slate-300">
                  {selectedTemplate.content}
                </div>
                <div className="mt-8 p-6 bg-indigo-50 dark:bg-indigo-950/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 flex gap-4 items-start">
                  <Info className="text-indigo-500 shrink-0" size={20} />
                  <p className="text-[10px] text-indigo-700 dark:text-indigo-300 leading-relaxed font-bold uppercase tracking-widest">
                    Estes modelos são sugestões técnicas. Recomendamos que adapte o conteúdo às especificidades do seu conselho regional e ao seu método de trabalho.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-10 sm:p-20 bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] sm:rounded-[3.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <FolderOpen size={64} className="text-slate-300 dark:text-slate-700 mb-6 opacity-30" />
              <h3 className="text-2xl font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest tracking-tighter">Selecione um Modelo</h3>
              <p className="text-sm text-slate-400 mt-4 max-w-xs font-medium italic leading-relaxed">Navegue pelos documentos à esquerda para visualizar modelos de contratos, fichas e laudos prontos para uso.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Repository;
